import React, { Fragment, useState, useContext, useEffect, useMemo } from "react";
import { Link, useLocation, useParams } from "wouter";
import { gql, useQuery } from "urql";
import { cloneDeep } from "lodash";
import stableStringify from "json-stable-stringify";

import { emptyObjectId } from "../../../common/src";
import { numberFormat } from "../../../common/src/utils/FormatFunctions";
import { CategoryBarChart } from "../../../components/src/charts/CategoryBarChart";
import { axisLabel, formatterFromTickformat } from "../../../components/src/charts/chartTheme";

import { Page } from "../../../components/src/Page";
import { useQueryState } from "../../../components/src/use-query-state";
import { ClientContextSelector } from "../contexts/ClientContextSelector";
import { ClientContext } from "../../../components/src/contexts/ClientContext";
import { PartyType } from "../types.generated";
import { LimitReportGrid, LimitReportInfo, ReportRow } from "../components/LimitReportGrid";

const GET_LIMIT_REPORT = gql`
    query report($clientId: GraphQLObjectId!, $date: GraphQLDateString) {
        party(_id: $clientId) {
            name
        }

        LimitReportV3: reports(clientId: $clientId, type: "LimitReportV3", name: "LimitReportV3", date: $date, lastOnly: true) {
            _id
            date
            type
            createTimestamp
            data
        }

        VaRLimitReport: reports(clientId: $clientId, type: "LimitReportData", name: "VaRLimitReport", date: $date, lastOnly: true) {
            _id
            date
            type
            createTimestamp
            data
        }
    }
`;

function StoredBarChart({ plot }: { plot: { data?: any[]; layout?: any } }): React.ReactElement {
    const traces = Array.isArray(plot?.data) ? plot.data : [];
    const rows = [];
    for (const trace of traces) {
        const categories = trace.x ?? [];
        const values = trace.y ?? [];
        for (let i = 0; i < categories.length; i++) {
            rows.push({
                category: String(categories[i]),
                series: String(trace.name || "Value"),
                value: Number(values[i])
            });
        }
    }
    const layout = plot?.layout ?? {};
    const tickformat = layout.yaxis?.tickformat;
    return (
        <CategoryBarChart
            rows={rows}
            orientation="vertical"
            mode={layout.barmode === "stack" ? "stack" : "group"}
            showLegend={layout.showlegend !== false}
            valueFormat={formatterFromTickformat(typeof tickformat === "string" ? tickformat : ".0%")}
            valueLabel={axisLabel(layout.yaxis?.title)}
            ariaLabel={axisLabel(layout.title) || "Limit report"}
            height={360}
        />
    );
}

const parseData = (
    limitRows: any[],
    reportDate: string,
    varReport: { date: any; data: string | any[] },
    party: { name: string }
): { date: string; name: string; rows: ReportRow[] } => {
    const rows = [];

    limitRows.forEach((row) => {
        rows.push({ ...row.limit, ...row.result });
    });

    if (varReport) {
        if (reportDate === varReport.date) {
            if (Array.isArray(varReport.data) && varReport.data.length) {
                const row = {
                    name: "Value at Risk",
                    type: "VaR",
                    value: varReport.data[0].vaR,
                    url: "https://en.wikipedia.org/wiki/Value_at_risk",
                    comment: "VaR",
                    maxValue: 0.03
                };
                // Special case for Iris
                if (varReport.data.length === 2 && party.name === "Captor Iris Bond") {
                    row.name = "Value at Risk vs comparison index";
                    row.type = "Relative VaR";
                    row.value = varReport.data[0].vaR / varReport.data[1].vaR;
                    row.maxValue = 1.3;
                    row.url = "https://captor.se/wp-content/uploads/2019/01/Captor-Iris-Bond-Fondbest%C3%A4mmelser-20190201.pdf";
                    row.comment = "Fund Specific";
                }
                // Special case for our Funds
                if (
                    varReport.data.length === 1 &&
                    (party.name === "Captor Aster Global Credit" ||
                        party.name === "Captor Aster Global Credit Short Term" ||
                        party.name === "Captor Aster Global High Yield" ||
                        party.name === "Captor Global Fixed Income" ||
                        party.name === "Captor Perenne Short Term Bond" ||
                        party.name === "Captor Dahlia Green Bond" ||
                        party.name === "Captor Scilla Global Equity")
                ) {
                    row.value = Math.abs(varReport.data[0].vaR / varReport.data[0].thVal);
                }
                // Max var different for Bliwa
                if (party.name === "Bliwa Liv") row.maxValue = 0.09;
                if (party.name === "Bliwa Skade") row.maxValue = 0.07;
                rows.push(row);
            }
        }
    }

    return {
        date: reportDate,
        name: party.name,
        rows: rows
    };
};

export const Limits = ({ clientId }: { clientId: string }): React.ReactElement => {
    const [endDate, setDateQueryArg] = useQueryState("endDate", "2000-01-01");
    const date = endDate === "2000-01-01" ? null : endDate;

    const { tabId } = useParams<{ tabId: string }>();
    const [location] = useLocation();

    const [variables, setVariables] = useState<{ clientId: string; date: string }>(null);

    const [{ fetching, error, data }] = useQuery({
        query: GET_LIMIT_REPORT,
        requestPolicy: "cache-and-network",
        variables: variables,
        pause: variables ? false : true
    });

    useEffect(() => {
        if (stableStringify({ clientId, date }) !== stableStringify(variables)) {
            setVariables({ clientId, date });
        }
    }, [clientId, date, variables]);

    useEffect(() => {
        if (!date && data && "LimitReportV3" in data && data.LimitReportV3.length > 0) {
            const [pathname] = location.split("?");
            //Only set query args in relevant tab or if on limit page
            if (
                date !== data.LimitReportV3[0].date &&
                ((tabId && tabId === "limits" && pathname.includes("portfoliorisk")) || !pathname.includes("portfoliorisk"))
            ) {
                setDateQueryArg(data.LimitReportV3[0].date);
            }
        }
    }, [data, date, setDateQueryArg, tabId, location]);

    const limitReportInfo = useMemo(() => {
        const defaultReportData: LimitReportInfo = {
            name: "",
            header: "",
            minValueWarningExist: false,
            maxValueWarningExist: false,
            rows: [],
            plot: null
        };
        const limitReportInfo = cloneDeep(defaultReportData);

        if (!data || !data.LimitReportV3 || data.LimitReportV3.length === 0) return limitReportInfo;

        const report = data.LimitReportV3[0];

        let limitRows = [];
        if (Array.isArray(report.data)) {
            limitRows = report.data;
        } else {
            if ("limit" in report.data) {
                limitRows = report.data["limit"];
            }
            if ("plot" in report.data) {
                limitReportInfo.plot = report.data["plot"];
            }
        }

        let varLimitReport = null;
        if (data.VaRLimitReport && data.VaRLimitReport.length > 0) {
            varLimitReport = data.VaRLimitReport[0];
        }

        const { name, rows } = parseData(limitRows, report.date, varLimitReport, data.party);
        limitReportInfo.name = name;
        limitReportInfo.rows = rows;
        limitReportInfo.header = "Limit report for " + data.party.name;

        // loop limits to set format
        for (const row of rows) {
            if (row.minValueWarning) limitReportInfo.minValueWarningExist = true;
            if (row.maxValueWarning) limitReportInfo.maxValueWarningExist = true;
            // not bulletproof but for now
            if (row.valueField === "interestRateYieldDelta") {
                if (row.value) row.value = numberFormat(Number(row.value), "#\xa0##0.0");
                if (row.minValueWarning) row.minValueWarning = numberFormat(Number(row.minValueWarning), "#\xa0##0.0");
                if (row.maxValueWarning) row.maxValueWarning = numberFormat(Number(row.maxValueWarning), "#\xa0##0.0");
                if (row.minValue) row.minValue = numberFormat(Number(row.minValue), "#\xa0##0.0");
                if (row.maxValue) row.maxValue = numberFormat(Number(row.maxValue), "#\xa0##0.0");
            }
        }

        return limitReportInfo;
    }, [data]);

    if (fetching) return <div>Loading...</div>;
    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (!data) {
        return <div></div>;
    }

    if (data.LimitReportV3.length === 0) {
        if (variables && variables.clientId && variables.clientId !== emptyObjectId) {
            if (data.LimitReportV3.length === 0) {
                return <Page header="Could not find a report" />;
            }
        } else {
            return <Page header="" />;
        }
    }

    return (
        <Page header={limitReportInfo.name}>
            <Fragment>
                <Link to={"/report/" + data.LimitReportV3[0]._id}>
                    <div>Date: {data.LimitReportV3[0].date}</div>
                </Link>
                <LimitReportGrid limitReportInfo={limitReportInfo} />
                {limitReportInfo.plot ? (
                    <div className="bg-gray-100 p-4 rounded mt-5">
                        <StoredBarChart plot={limitReportInfo.plot} />
                    </div>
                ) : null}
            </Fragment>
        </Page>
    );
};

export const LimitReportPage = (): React.ReactElement => {
    const { client } = useContext(ClientContext);

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-4/12 px-2">
                    <ClientContextSelector typeIn={[PartyType.Client]} />
                </div>
            </div>
            {client ? <Limits clientId={client._id} /> : null}
            <div className="flex flex-wrap -mx-2 mt-5">
                <div className="w-full md:w-4/12 px-2"></div>
            </div>
        </div>
    );
};
